import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { useTranslation } from "react-i18next";
import type { Api } from "../../../services/api";
import { PublicationCard } from "../../ui/PublicationCard";
import { PubMedPublicationCard } from "../../ui/PubMedPublicationCard";
import { snapshotDesign } from "../../../domains/gpt/chatSource";
import { getArticleTypeVariant } from "../../../domains/app/utils/getArticleTypeText";
import { GptModeBadge } from "./GptModeBadge";
import { isDesktopShell } from "../../../common/hooks/useIsDesktopView";

// The Search-mode turn payload (docs/gpt-mode-router.md), inferred from the
// search endpoint — never a custom shape. `docs` are the SAME article cards the
// /search page renders, so the chat shows the same rows.
type SearchTurnBody =
  Api["post"]["/webapp/private/conversations/:id/search"]["output"]["body"];
export type GptSearchDoc = SearchTurnBody["docs"][number];
// A PubMed record of the turn's PubMed section: the source snapshot the General
// chat already persists for PubMed, so the two surfaces read one shape.
export type GptSearchPubmedDoc = SearchTurnBody["pubmedDocs"][number];

// The PubMed records shown before "show more": enough to see what PubMed adds
// without pushing the refinement chips far below the Juisci results.
const PUBMED_VISIBLE_COUNT = 3;

// Up to three names, unlike the "X et al." of the /search rows. Takes the full
// ordered list: an article's caller puts `primary_author` first.
function authorLine(authors: ReadonlyArray<string>): string {
  if (authors.length === 0) return "";
  if (authors.length <= 3) return authors.join(", ");
  return `${authors.slice(0, 3).join(", ")} et al.`;
}

function publicationYear(value: Date | string | null): string {
  if (value === null) return "";
  const year = new Date(value).getUTCFullYear();
  return Number.isNaN(year) ? "" : String(year);
}

function PubmedDocList({
  docs,
}: {
  docs: ReadonlyArray<GptSearchPubmedDoc>;
}): React.JSX.Element {
  return (
    <ul className="flex w-full flex-col divide-y divide-ds-black/6">
      {docs.map((doc) => {
        const design = snapshotDesign(doc.studyDesign);
        return (
          <li key={doc.externalId} className="w-full py-4 first:pt-0 last:pb-0">
            <PubMedPublicationCard
              // The desktop shell reads the records as the desktop Explore's
              // rows; the phone keeps its cards.
              layout={isDesktopShell() ? "row" : "card"}
              title={doc.title}
              journalName={doc.journal}
              author={authorLine(doc.authors)}
              year={doc.year}
              design={design === null ? null : design.label}
              url={doc.url}
              // Same ground as the Juisci rows above: the chat transcript.
              className="bg-transparent"
            />
          </li>
        );
      })}
    </ul>
  );
}

// The PubMed section under the Juisci results (proposal D): one separator names
// the source for the whole group, so each card needs only two small marks.
function PubmedSection({
  docs,
}: {
  docs: ReadonlyArray<GptSearchPubmedDoc>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const visible = docs.slice(0, PUBMED_VISIBLE_COUNT);
  const hidden = docs.slice(PUBMED_VISIBLE_COUNT);
  return (
    <section className="flex flex-col gap-3 pt-2">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2.5 font-dm-sans text-caption text-ds-slate-600">
          <span className="h-px flex-1 bg-ds-slate-200" />
          <span>
            <span className="font-semibold text-ds-slate-700">
              {t("From PubMed")}
            </span>
            {" · "}
            {t("{{count}} results", { count: docs.length })}
          </span>
          <span className="h-px flex-1 bg-ds-slate-200" />
        </div>
        <p className="text-center font-dm-sans text-caption text-ds-slate-500">
          {t("Outside the Juisci library. These articles open on PubMed.")}
        </p>
      </div>
      <PubmedDocList docs={visible} />
      {hidden.length === 0 ? null : (
        // A native disclosure: the open state lives in the DOM, not in React.
        <details className="group flex flex-col">
          <summary className="flex cursor-pointer list-none justify-center group-open:hidden [&::-webkit-details-marker]:hidden">
            <span className="rounded-full border border-solid border-ds-slate-200 bg-ds-white px-3 py-1 font-dm-sans text-ui-note font-medium text-ds-mint-900 hover:bg-ds-slate-100">
              {t("Show {{count}} more PubMed results", {
                count: hidden.length,
              })}
            </span>
          </summary>
          <div className="border-t border-ds-black/6 pt-4">
            <PubmedDocList docs={hidden} />
          </div>
        </details>
      )}
    </section>
  );
}

/**
 * Renders a Search (find_publication) turn inline in the chat transcript: the
 * mode badge, a one-line framing, the found articles as the SAME `PublicationCard`
 * rows the /search page uses, and "search deeper" refinement chips.
 */
export function GptSearchTurn({
  framing,
  docs,
  pubmedDocs,
  refinements,
  onOpenArticle,
  onRefine,
  onAskGeneral,
  showModeBadge,
}: {
  framing: string;
  docs: ReadonlyArray<GptSearchDoc>;
  // Empty when the turn ran no PubMed search (flag off at send time, or a turn
  // persisted before the section existed).
  pubmedDocs: ReadonlyArray<GptSearchPubmedDoc>;
  refinements: ReadonlyArray<string>;
  onOpenArticle: (article: GptSearchDoc) => void;
  onRefine: (refinement: string) => void;
  // Re-ask the SAME question in General mode, which grounds on the wider
  // scientific literature (Exa web + PubMed) instead of only the Juisci library.
  // Null on read-only surfaces (shared view, chat-with-paper) that cannot start a
  // turn: the recovery button is then dropped, like the refinement chips.
  onAskGeneral: (() => void) | null;
  // False where the desktop context panel names the turn's mode already.
  showModeBadge: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  // The PubMed section is behind EXPERIMENTAL, on this surface and on a shared
  // link alike. Reactive read: the section appears once the flags load.
  const isPubmedSectionEnabled = useFeatureIsOn("EXPERIMENTAL");

  return (
    <div className="flex flex-col gap-3 px-5">
      {showModeBadge && <GptModeBadge mode="find_publication" />}
      {framing !== "" && (
        <p className="font-dm-sans text-ui-body text-ds-black">{framing}</p>
      )}
      {docs.length === 0 ? (
        // Zero internal matches is a dead end on its own: the Juisci library is a
        // subset of the literature. Offer to re-ask the same question in General
        // mode, which grounds on the wider scientific literature.
        <div className="flex flex-col items-start gap-2">
          <p className="font-dm-sans text-ui-note text-ds-slate-600">
            {t("No publication in the Juisci library matches this search.")}
          </p>
          {onAskGeneral !== null && (
            <button
              type="button"
              onClick={onAskGeneral}
              className="cursor-pointer rounded-full border border-solid border-ds-slate-200 bg-ds-white px-3 py-1 font-dm-sans text-ui-note font-medium text-ds-mint-900 hover:bg-ds-slate-100"
            >
              {t(
                "Ask JuisciGPT to answer from the wider scientific literature"
              )}
            </button>
          )}
        </div>
      ) : (
        <ul className="flex w-full flex-col divide-y divide-ds-black/6">
          {docs.map((article) => {
            const journal = article.journal;
            return (
              <li key={article.id} className="w-full py-4 first:pt-0 last:pb-0">
                <PublicationCard
                  isInPlayer={false}
                  title={article.title}
                  journalName={journal === null ? "" : journal.name}
                  // `authors` holds the co-authors: the first author is
                  // `primary_author`, so it leads the line. Older works still
                  // repeat it inside `authors`, hence the filter.
                  author={authorLine(
                    article.primary_author === null
                      ? article.authors
                      : [
                          article.primary_author,
                          ...article.authors.filter(
                            (author) => author !== article.primary_author
                          ),
                        ]
                  )}
                  date={publicationYear(article.publication_date)}
                  meta={
                    article.article_type === null
                      ? t("Article")
                      : article.article_type
                  }
                  metaVariant={getArticleTypeVariant(article.article_type)}
                  access={article.isOpenAccess === true ? "open" : "closed"}
                  recommendation={null}
                  isNew={false}
                  isBookmarked={false}
                  onBookmark={null}
                  onShare={null}
                  onClick={() => {
                    onOpenArticle(article);
                  }}
                  imageUrl={null}
                  description={null}
                  onJournalClick={null}
                  onRecommendationClick={null}
                  // The chat transcript is the card's ground here: the row sits
                  // straight on the conversation surface, with no white plate.
                  className="bg-transparent md:hover:bg-ds-black/4"
                />
              </li>
            );
          })}
        </ul>
      )}
      {isPubmedSectionEnabled && pubmedDocs.length > 0 && (
        <PubmedSection docs={pubmedDocs} />
      )}
      {refinements.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <p className="font-dm-sans text-ui-note font-medium text-ds-slate-600">
            {t("Search deeper")}
          </p>
          <div className="flex flex-wrap gap-2">
            {refinements.map((refinement) => (
              <button
                key={refinement}
                type="button"
                onClick={() => {
                  onRefine(refinement);
                }}
                className="cursor-pointer rounded-full border border-solid border-ds-slate-200 bg-ds-white px-3 py-1 font-dm-sans text-ui-note font-medium text-ds-mint-900 hover:bg-ds-slate-100"
              >
                {refinement}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
